import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { PagedList, PermissionModel } from 'hx-services';
import { HxOrgSelectComponent, HxPagedListDirective, HxStoreSelectComponent, HxUserSelectComponent } from 'hx-components';
import { PermissionService } from '@adm-web-app/services/permission.service';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { NzPageHeaderComponent, NzPageHeaderExtraDirective, NzPageHeaderSubtitleDirective, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzButtonComponent, NzButtonGroupComponent } from 'ng-zorro-antd/button';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzCollapseComponent, NzCollapsePanelComponent } from 'ng-zorro-antd/collapse';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { FormsModule } from '@angular/forms';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import { NzTableCellDirective, NzTableComponent, NzTbodyComponent, NzThAddOnComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { NzSwitchComponent } from 'ng-zorro-antd/switch';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import { NzDropdownMenuComponent } from 'ng-zorro-antd/dropdown';
import { NzCheckboxGroupComponent } from 'ng-zorro-antd/checkbox';

interface ListFilter {
  name: string;
  options: { label: string, value: string | number, checked?: boolean }[];
  isVisible: boolean;
  isActive: boolean;
  operator: 'and' | 'or';
}

@Component({
    selector: 'hc-permission-list',
    templateUrl: './permission-list.component.html',
    styleUrls: ['./permission-list.component.less'],
  imports: [TranslocoDirective, NzAffixComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzPageHeaderSubtitleDirective, NzPageHeaderExtraDirective, NzButtonComponent, RouterLink, NzIconDirective, NzCollapseComponent, NzCollapsePanelComponent, NzRowDirective, NzColDirective, NzSelectComponent, FormsModule, NgFor, NzOptionComponent, NgIf, HxStoreSelectComponent, HxUserSelectComponent, HxOrgSelectComponent, NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzThAddOnComponent, NzTbodyComponent, NzSwitchComponent, NzButtonGroupComponent, NzWaveDirective, NzPopconfirmDirective, NzDropdownMenuComponent, NzCheckboxGroupComponent, DatePipe, TranslocoPipe]
})
export class PermissionListComponent extends HxPagedListDirective<PermissionModel> implements OnInit {
  storesFilter: ListFilter = {
    options: [], isActive: false, isVisible: false, operator: 'or', name: 'storeIds'
  };
  isLoading: {
    permissions: { [key: number]: boolean }
  } = {
    permissions: {}
  };
  containerId?: number;
  containerClass?: string;
  containerClassOptions = ['User', 'Store', 'Organization'];
  /** The backend said 403: the section is for administrators; one line instead of an empty table. */
  forbidden = false;

  constructor(
    override router: Router,
    override aRoute: ActivatedRoute,
    private permissionService: PermissionService,
  ) {
    super(router, aRoute);
  }

  ngOnInit() {
    this.aRoute.queryParamMap.pipe(takeUntil(this.$destroyed))
      .subscribe(paramMap => {
        const asNumberFn = (name: string, def?: number) => paramMap.get(name) ? Number(paramMap.get(name)) : def;
        // const asBooleanFn = (name: string, def?: boolean) => paramMap.get(name) ? paramMap.get(name) === 'true' : def;
        this.containerId = asNumberFn('containerId');
        this.containerClass = paramMap.get('containerClass') || undefined;
        super.load(paramMap);
      });
  }

  resetFilter(filter: ListFilter) {
    filter.options.forEach(op => op.checked = false);
  }

  applyFilters() {
    this.router.navigate([], {
      relativeTo: this.aRoute,
      queryParams: {
        containerId: this.containerId,
        containerClass: this.containerClass,
      },
      queryParamsHandling: 'merge',
    });
  }

  applyFilter(filter: ListFilter) {
    const checkedArr = filter.options.filter(v => v.checked).map(v => v.value);
    let filterParam: string | null = null;
    let filterOp: 'and' | 'or' | null = null;
    if (checkedArr.length !== 0) {
      filterParam = checkedArr.join(',');
      filterOp = filter.operator;
    }
    const queryParams: Params = {};
    queryParams[filter.name] = filterParam;
    queryParams[filter.name + '_op'] = filterOp;
    filter.isVisible = false;
    this.router.navigate([], {
      relativeTo: this.aRoute,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  protected async loadList(): Promise<PagedList<PermissionModel>> {
    try {
      return await this.permissionService.getPermissions({
        page: this.page,
        limit: this.limit,
        sort: this.getSortParams(),
        containerId: this.containerId,
        containerClass: this.containerClass,
        // key: this.key,
      });
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 403) {
        this.forbidden = true;
        return { count: 0, list: [] };
      }
      throw err;
    }
  }

  remove(el: PermissionModel) {
    this.permissionService.removePermission(el.id)
      .then(() => this.loadData());
  }

  async toggleActive(data: PermissionModel) {
    this.isLoading.permissions[data.id] = true;
    try {
      await this.permissionService.setActive(data.id, !data.active);
      data.active = !data.active;
    } finally {
      this.isLoading.permissions[data.id] = false;
    }
  }

  clearContainerId() {
    this.containerId = undefined;
    this.applyFilters();
  }
}
